import { ControlMetadata } from '@amzn/controllershiphub-typescript-client';
import {
  Alert,
  Button,
  Container,
  Grid,
  SpaceBetween,
  Spinner,
} from '@cloudscape-design/components';
import { useFlashbar } from 'src/common/hooks/useFlashbar';
import React, { useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { NavigationPaths } from 'src/common/config/ApiConstants';
import { BUTTON_TYPES, GRC5253_CONTROL_ID } from 'src/constants/grc5253';
import { buildLeaseTypeUrlParam, isNonProdEnvironment } from 'src/utils/ApiCall';
import { useControls } from 'src/api/hooks/useControlData';
import { useUserAlias } from 'src/common/hooks/useUserAlias';
import { PageHeader } from 'src/components/layout/PageHeader';
import ControlDescription from 'src/utils/ControlDescription';
import PageLayout from 'src/components/common/PageLayout/PageLayout';

/**
 * ControlHomePage Component
 *
 * This is a generic component that generates a Control Home page for any control
 * within the Controllership Hub. It dynamically renders content based on the
 * Control ID provided in the URL.
 *
 * The component utilizes custom hooks to retrieve the user alias and the complete
 * list of available controls. It then identifies and displays the specific control
 * information by matching the Control ID from the URL parameters with the control
 * list.
 *
 * This approach allows for a single, reusable component to handle the display of
 * any control's home page, enhancing maintainability and consistency across the
 * application.
 */
export const ControlHomePage = () => {
  // Get the user alias using a custom hook
  const { alias } = useUserAlias();

  // Get the list of controls using the React Query hook
  const { data, isLoading, error } = useControls();
  const controls = data?.controls ?? [];

  // Extract the controlId from the URL parameters
  const { controlId } = useParams<{ controlId: string }>();

  /**
   * Memoized function to find the current control
   *
   * This function searches for a control in the controls array that matches the controlId from the URL.
   * It returns the found control object or null if no match is found.
   * The function is memoized to optimize performance, only recalculating when controls or controlId change.
   */
  const control = useMemo(() => {
    return controls.find((c) => c.controlId === controlId) || null;
  }, [controls, controlId]);

  // Import the useNavigate hook from react-router-dom (assumed)
  const navigate = useNavigate();

  // Check if current environment is non-production (beta or gamma)
  const isNonProd = isNonProdEnvironment();

  /**
   * Handles the click event for a control button.
   *
   * @param {ControlMetadata | null} control - The metadata of the clicked control, or null if no control is associated.
   * @param {'AWS' | 'STORES'} buttonType - The type of button clicked (default: 'AWS')
   */
  const handleButtonClick = (
    control: ControlMetadata | null,
    buttonType: typeof BUTTON_TYPES.AWS | typeof BUTTON_TYPES.STORES = BUTTON_TYPES.AWS
  ) => {
    // For STORES button behavior depends on environment
    if (buttonType === BUTTON_TYPES.STORES && (control as any)?.storesExternalLink) {
      // In prod: always use external link (original behavior)
      // In beta/gamma: navigate to summary page with leaseType filter
      if (!isNonProd) {
        window.open((control as any).storesExternalLink, '_blank', 'noopener,noreferrer');
        return;
      }
      // Fall through to internal navigation for beta/gamma
    }

    // For AWS button with external link, open in new tab
    if (buttonType === BUTTON_TYPES.AWS && control?.externalLink) {
      window.open(control.externalLink, '_blank', 'noopener,noreferrer');
      return;
    }

    // For internal navigation - beta/gamma only for STORES
    if (control) {
      // Format the control type: convert to lowercase and replace underscores
      const controlType = control.controlType?.toLowerCase().replace(/_/g, '_');

      // Only add leaseType parameter for GRC5253 control
      const isGrc5253 = control.controlId === GRC5253_CONTROL_ID;
      const leaseTypeParam = isGrc5253 ? buildLeaseTypeUrlParam(buttonType) : '';

      // Navigate to the summary page, with leaseType parameter only for GRC5253
      navigate(`/${controlType}/${control.controlId}${NavigationPaths.summary}${leaseTypeParam}`);
    } else {
      // If control is null, navigate to the home page as a fallback
      navigate('/');
    }
  };

  const { addFlashbarMessage } = useFlashbar();

  return (
    <>
      <PageLayout defaultPadding disableOverlap>
        {isLoading ? (
          <Spinner />
        ) : error ? (
          <Alert type="error">Error loading controls: {error.message}</Alert>
        ) : controls.length > 0 ? (
          <>
            <h1>
              <b>{control?.controlName || 'No control name'}</b>
            </h1>
            <h2>{control?.controlSubTitle || 'No control SubTitle'}</h2>
            <h3 data-testid="control-description">
              <ControlDescription description={control?.controlDescription || ''} />
            </h3>
            <SpaceBetween size="m">
              {/* Control Disclaimer - Shown if available */}
              {control?.controlDisclaimer && (
                <Alert statusIconAriaLabel="Warning" type="warning">
                  {control?.controlDisclaimer}
                </Alert>
              )}
              <Grid>
                {/* Dual Button Logic for GRC5253 - STORES on left, AWS on right */}
                {(control as any)?.storesUserAuthorized !== undefined ? (
                  <SpaceBetween direction="horizontal" size="s">
                    {/* STORES Button - Left position */}
                    <Button
                      variant="primary"
                      disabled={isNonProd && !(control as any)?.storesUserAuthorized}
                      onClick={() => handleButtonClick(control, BUTTON_TYPES.STORES)}
                      {...(!isNonProd && (control as any)?.storesExternalLink
                        ? {
                            iconAlign: 'right',
                            iconName: 'external',
                          }
                        : {})}
                    >
                      Perform {control?.controlName || 'No control name'} - STORES
                    </Button>
                    {/* AWS Button - Right position */}
                    <Button
                      variant="primary"
                      iconAlign="right"
                      disabled={!control?.userAuthorized}
                      onClick={() => handleButtonClick(control, BUTTON_TYPES.AWS)}
                    >
                      Perform {control?.controlName || 'No control name'} - AWS
                    </Button>
                  </SpaceBetween>
                ) : (
                  /* Button for all other controls */
                  <Button
                    variant="primary"
                    iconAlign="right"
                    disabled={!control?.userAuthorized && !control?.externalLink}
                    onClick={() => handleButtonClick(control)}
                    {...(control?.externalLink
                      ? {
                          target: '_blank',
                          rel: 'noopener noreferrer',
                          iconName: 'external',
                        }
                      : {})}
                  >
                    Perform {control?.controlName || 'No control name'}
                  </Button>
                )}
              </Grid>
            </SpaceBetween>
          </>
        ) : (
          <Alert type="info">No controls available</Alert>
        )}
      </PageLayout>
    </>
  );
};
